import AsyncStorage from "@react-native-async-storage/async-storage";
import { createMMKV } from "react-native-mmkv";
import type { PersistStorage, StateStorage, StorageValue } from "zustand/middleware";

const mmkv = createMMKV({ id: "trovie-client-state" });
const generations = new Map<string, number>();
const MIGRATED_PREFIX = "async-storage-migrated:";

function bump(name: string) {
  generations.set(name, (generations.get(name) ?? 0) + 1);
}

function migrated(name: string) {
  return mmkv.getString(MIGRATED_PREFIX + name) !== undefined;
}

/** Drop the legacy key once and remember it, so later reads and writes stay MMKV-only. */
function retireLegacy(name: string) {
  if (migrated(name)) return;
  mmkv.set(MIGRATED_PREFIX + name, "1");
  void AsyncStorage.removeItem(name).catch(() => {});
}

/**
 * Synchronous MMKV-backed storage for client state. A first read falls back to the old
 * AsyncStorage key and copies it forward, so existing device preferences survive the migration.
 */
export const clientStorage: StateStorage = {
  getItem: (name) => {
    const current = mmkv.getString(name);
    if (current !== undefined) return current;
    if (migrated(name)) return null;

    const generation = generations.get(name) ?? 0;
    return AsyncStorage.getItem(name)
      .then((legacy) => {
        if ((generations.get(name) ?? 0) !== generation) {
          return mmkv.getString(name) ?? null;
        }
        if (legacy !== null) mmkv.set(name, legacy);
        retireLegacy(name);
        return legacy;
      })
      .catch(() => null);
  },
  setItem: (name, value) => {
    bump(name);
    mmkv.set(name, value);
    retireLegacy(name);
  },
  removeItem: (name) => {
    bump(name);
    mmkv.remove(name);
    retireLegacy(name);
  },
};

function decodeValue<T>(raw: string | null, decode: (value: string) => T | null) {
  if (raw === null) return null;
  const state = decode(raw);
  return state === null ? null : { state } satisfies StorageValue<T>;
}

/** Preserve the app's existing compact on-device formats while Zustand owns hydration/writes. */
export function createRawPersistStorage<T>(
  decode: (value: string) => T | null,
  encode: (state: T) => string,
): PersistStorage<T> {
  return {
    getItem: (name) => {
      const raw = clientStorage.getItem(name);
      return raw instanceof Promise
        ? raw.then((value) => decodeValue(value, decode))
        : decodeValue(raw, decode);
    },
    setItem: (name, value) => clientStorage.setItem(name, encode(value.state)),
    removeItem: (name) => clientStorage.removeItem(name),
  };
}
